import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Monitor, RefreshCw, Clock, Eye, EyeOff, Copy } from "lucide-react";

export const DemoManagementCard = () => {
  const [demo, setDemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const token = localStorage.getItem("fm_admin_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchDemo = async () => {
    try {
      const res = await fetch("/api/admin/demo", { headers });
      if (res.ok) setDemo(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchDemo(); }, []);

  const updateDemo = async (data) => {
    setUpdating(true);
    try {
      const res = await fetch("/api/admin/demo", { method: "PUT", headers, body: JSON.stringify(data) });
      if (res.ok) { toast.success("Demo-Einstellungen aktualisiert"); fetchDemo(); }
      else toast.error("Fehler beim Aktualisieren");
    } catch (e) { toast.error("Netzwerkfehler"); }
    setUpdating(false);
  };

  const resetDemo = async () => {
    setUpdating(true);
    try {
      const res = await fetch("/api/admin/demo/reset", { method: "POST", headers });
      if (res.ok) { toast.success("Demo-Timer zurückgesetzt"); fetchDemo(); }
      else toast.error("Fehler beim Zurücksetzen");
    } catch (e) { toast.error("Netzwerkfehler"); }
    setUpdating(false);
  };

  const copyCredentials = () => {
    const text = `Demo-Zugang Business Studio Suite\nURL: https://business-studio-suite.app\nE-Mail: ${demo?.email}\nPasswort: ${demo?.password}`;
    navigator.clipboard.writeText(text);
    toast.success("Zugangsdaten kopiert!");
  };

  if (loading) return <Card className="p-5 border-[var(--brand-border)]"><p className="text-sm text-[var(--brand-muted)]">Lade Demo-Status...</p></Card>;
  if (!demo?.exists) return null;

  return (
    <Card className="p-5 border-[var(--brand-border)]" data-testid="demo-management-card">
      <div className="flex items-start gap-2 mb-4">
        <Monitor className="w-5 h-5 text-[var(--brand-primary)] mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-[var(--brand-ink)]">Demo-Zugang verwalten</div>
          <p className="text-xs text-[var(--brand-muted)]">Zeitlich begrenzter Zugang für Interessenten. Du bekommst eine Benachrichtigung bei jedem Login.</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchDemo} className="border-[var(--brand-border)]">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Status */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-gray-50 rounded p-2 text-center">
          <div className="text-xs text-[var(--brand-muted)]">Status</div>
          <div className={`text-sm font-semibold ${demo.active ? "text-green-600" : "text-red-600"}`}>
            {demo.active ? "Aktiv" : "Deaktiviert"}
          </div>
        </div>
        <div className="bg-gray-50 rounded p-2 text-center">
          <div className="text-xs text-[var(--brand-muted)]">Modus</div>
          <div className="text-sm font-semibold text-[var(--brand-ink)]">
            {demo.mode === "permanent" ? "Dauerhaft" : `${demo.hours}h Limit`}
          </div>
        </div>
        <div className="bg-gray-50 rounded p-2 text-center">
          <div className="text-xs text-[var(--brand-muted)]">Logins</div>
          <div className="text-sm font-semibold text-[var(--brand-ink)]">{demo.login_count || 0}</div>
        </div>
        <div className="bg-gray-50 rounded p-2 text-center">
          <div className="text-xs text-[var(--brand-muted)]">Restzeit</div>
          <div className={`text-sm font-semibold ${demo.expired ? "text-red-600" : "text-[var(--brand-ink)]"}`}>
            {demo.mode === "permanent" ? "\u221E" : demo.expired ? "Abgelaufen" : demo.time_remaining_hours != null ? `${demo.time_remaining_hours}h` : "Noch nicht gestartet"}
          </div>
        </div>
      </div>

      {/* Credentials */}
      <div className="bg-[var(--brand-surface)] border border-[var(--brand-border)] rounded p-3 mb-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-[var(--brand-muted)]">Zugangsdaten zum Weitergeben:</span>
          <Button variant="ghost" size="sm" onClick={copyCredentials} className="text-xs">
            <Copy className="w-3 h-3 mr-1" /> Kopieren
          </Button>
        </div>
        <div className="text-sm text-[var(--brand-ink)]">
          <span className="font-medium">E-Mail:</span> {demo.email}<br/>
          <span className="font-medium">Passwort:</span> {demo.password}
        </div>
      </div>

      {/* Last login info */}
      {demo.last_login && (
        <p className="text-xs text-[var(--brand-muted)] mb-4">
          Letzter Login: {new Date(demo.last_login).toLocaleString("de-DE")}
        </p>
      )}

      {/* Controls */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={demo.active ? "outline" : "default"}
          size="sm"
          onClick={() => updateDemo({ active: !demo.active })}
          disabled={updating}
          className={demo.active ? "border-red-200 text-red-700 hover:bg-red-50" : ""}
        >
          {demo.active ? <><EyeOff className="w-4 h-4 mr-1" /> Deaktivieren</> : <><Eye className="w-4 h-4 mr-1" /> Aktivieren</>}
        </Button>

        <Button variant="outline" size="sm" onClick={resetDemo} disabled={updating} className="border-[var(--brand-border)]">
          <Clock className="w-4 h-4 mr-1" /> Timer zurücksetzen
        </Button>

        {/* Mode toggle */}
        <select
          className="text-sm border border-[var(--brand-border)] rounded px-2 py-1"
          value={demo.mode}
          onChange={(e) => updateDemo({ mode: e.target.value })}
          disabled={updating}
        >
          <option value="timed">Zeitlich begrenzt</option>
          <option value="permanent">Dauerhaft</option>
        </select>

        {/* Hours selector */}
        {demo.mode === "timed" && (
          <select
            className="text-sm border border-[var(--brand-border)] rounded px-2 py-1"
            value={demo.hours}
            onChange={(e) => updateDemo({ hours: parseInt(e.target.value) })}
            disabled={updating}
          >
            <option value="24">24 Stunden</option>
            <option value="48">48 Stunden</option>
            <option value="72">72 Stunden</option>
          </select>
        )}
      </div>
    </Card>
  );
};
